import { useRequiredContext } from "@redotech/react-util/context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { isReturnRateSourceSelectionEnabled } from "@redotech/redo-return-core-common/shipping/return-rate-source-selection";
import { useQuery } from "@tanstack/react-query";
import { useContext } from "react";

export interface ReturnRateSourceDropoffIntegrations {
  blueYonderEnabled: boolean;
  happyReturnsEnabled: boolean;
}

const emptyReturnRateSourceDropoffIntegrations: ReturnRateSourceDropoffIntegrations =
  { blueYonderEnabled: false, happyReturnsEnabled: false };

export function getReturnRateSourceDropoffIntegrationsQueryKey(
  teamId?: string,
) {
  return ["returnRateSourceDropoffIntegrations", teamId ?? null] as const;
}

export function useReturnRateSourceDropoffIntegrations() {
  const team = useContext(TeamContext);
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const teamId = team?._id?.toString();
  const rateSourceSelectionEnabled = team
    ? isReturnRateSourceSelectionEnabled(team)
    : false;

  const query = useQuery({
    queryKey: getReturnRateSourceDropoffIntegrationsQueryKey(teamId),
    queryFn: async () => {
      const [blueYonderResult, happyReturnsResult] = await Promise.all([
        rpcClient.getBlueYonderIntegration({}),
        rpcClient.getHappyReturnsIntegration({}),
      ]);

      return {
        blueYonderEnabled:
          blueYonderResult.blueYonderIntegration?.enabled === true,
        happyReturnsEnabled:
          happyReturnsResult.happyReturnsIntegration?.enabled === true,
      };
    },
    enabled: rateSourceSelectionEnabled && !!teamId,
  });

  return {
    ...query,
    integrations: query.data ?? emptyReturnRateSourceDropoffIntegrations,
  };
}
